// Fetch de los productos (variantes) reales de la empresa del user para
// Interfaz V2. Reemplaza al mock `MOCK_USER_PRODUCTS`.
//
// La data vive en dos tablas:
//   • `variantes` — fila por variante de producto (id, nombre, …).
//   • `productos_imagenes` — filas con imágenes asociadas a una variante.
//
// Las imágenes en `productos_imagenes.url` son nombres de archivo (no URLs).
// El path completo en el bucket `productos` es:
//   `<companyId>/<variantId>/opt/<filename>`   (thumbnail)
//   `<companyId>/<variantId>/full/<filename>`  (full size)
// Si `url` empieza con `http`, es URL completa y se usa tal cual.

import { supabase } from '../../lib/supabase';
import type { MockProductV2 } from './mockData';

const PRODUCTOS_BUCKET = 'productos';
const SIGNED_URL_TTL = 3600; // 1h

interface RawImage {
  variante_id: string;
  url: string;
  principal: boolean | null;
}

/**
 * Trae las variantes activas de la empresa + la imagen principal de cada
 * una (signed URL). Mantiene el shape de MockProductV2 para que el resto
 * de la UI (pickers, chips, etc.) no necesite cambios.
 */
export async function fetchUserProducts(
  companyId: string,
): Promise<MockProductV2[]> {
  // Joineamos `productos!inner` para leer `model_view` (categoría del
  // producto padre — torso/calzado/cuerpo_completo/etc.). Lo usamos en
  // el product picker para sugerir productos según el template elegido.
  const { data: variants, error: varErr } = await supabase
    .from('variantes')
    .select('id, nombre, created_at, productos!inner(model_view)')
    .eq('activo', true)
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  if (varErr || !variants || variants.length === 0) return [];

  const variantIds = (variants as any[]).map((v) => v.id as string);
  const { data: imagesRaw } = await supabase
    .from('productos_imagenes')
    .select('variante_id, url, principal')
    .eq('id_company', companyId)
    .in('variante_id', variantIds);

  // Agrupar imágenes por variante. Preferimos la principal; sino, la
  // primera que aparece (ya viene ordenada por la query, aunque no
  // ordenamos explícitamente — para mejorar, se podría agregar order
  // by orden ASC).
  const byVariant = new Map<string, RawImage>();
  for (const img of ((imagesRaw || []) as any[]) as RawImage[]) {
    const existing = byVariant.get(img.variante_id);
    if (!existing) {
      byVariant.set(img.variante_id, img);
    } else if (img.principal && !existing.principal) {
      byVariant.set(img.variante_id, img);
    }
  }

  // Firmar URLs en paralelo para todas las variantes con imagen.
  const products: MockProductV2[] = await Promise.all(
    (variants as any[]).map(async (v): Promise<MockProductV2> => {
      const img = byVariant.get(v.id);
      let imageUrl = '';
      if (img?.url) {
        if (img.url.startsWith('http')) {
          imageUrl = img.url;
        } else {
          const optPath = `${companyId}/${v.id}/opt/${img.url}`;
          const { data: signed } = await supabase.storage
            .from(PRODUCTOS_BUCKET)
            .createSignedUrl(optPath, SIGNED_URL_TTL);
          if (signed?.signedUrl) {
            imageUrl = signed.signedUrl;
          } else {
            // Fallback al full si el opt no existe.
            const fullPath = `${companyId}/${v.id}/full/${img.url}`;
            const { data: signedFull } = await supabase.storage
              .from(PRODUCTOS_BUCKET)
              .createSignedUrl(fullPath, SIGNED_URL_TTL);
            imageUrl = signedFull?.signedUrl || '';
          }
        }
      }
      // `productos` viene del join — puede ser objeto o array según el
      // cliente. Normalizamos a string, recortamos whitespace y bajamos
      // a lowercase para que el match contra `tipos_producto_sugeridos`
      // sea robusto a typos de carga (algunos rows tienen "torso\n").
      const productosRel = (v as any).productos;
      const rawModelView = Array.isArray(productosRel)
        ? productosRel[0]?.model_view
        : productosRel?.model_view;
      const modelView =
        typeof rawModelView === 'string'
          ? rawModelView.trim().toLowerCase() || null
          : null;
      return {
        id: v.id as string,
        name: v.nombre as string,
        imageUrl,
        modelView,
      };
    }),
  );
  return products;
}
